import { useState, useEffect } from "react";
import { Label, Button, Skeleton, DatePicker } from "@pushpress/pushpress-ui";
import { CheckIcon, XIcon, Pencil } from "lucide-react";
import { format, getUnixTime } from "date-fns";
import { convertDateToUsDateFormat } from "../../helpers";

type EditableDateFieldProps = {
  label: string;
  value: Date;
  onSave: (value: string | number) => void;
  hasData?: boolean;
  validation?: (value: string) => boolean;
  dateField: "dob" | "memberSince" | "firstCheckinTimestamp";
};

/**
 * dob: yyyy-mm-dd string format in GET and PATCH
 * firstCheckinTimestamp: 10 digit timestamp in GET and PATCH
 * memberSince: ISO string in GET, 10 digit timestamp in PATCH
 */
export default function EditableDateField({
  label,
  value,
  onSave,
  hasData = false,
  dateField,
}: EditableDateFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    if (dateField === "firstCheckinTimestamp" || dateField === "memberSince") {
      onSave(getUnixTime(editValue));
    }
    if (dateField === "dob") {
      onSave(format(editValue, "yyyy-MM-dd"));
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  if (!hasData) {
    return (
      <div className="tw-group">
        <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
        <Skeleton className="tw-h-4 tw-w-1/2" />
      </div>
    );
  }

  return (
    <div className="tw-group tw-h-12">
      <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
      {isEditing ? (
        <div className="tw-flex tw-items-center">
          <DatePicker
            value={editValue}
            onValueChange={(date) => setEditValue(date)}
            className="tw-w-[330px]"
          />
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7"
            onClick={handleSave}
          >
            <CheckIcon className="tw-h-4 tw-w-4 tw-text-success" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7"
            onClick={handleCancel}
          >
            <XIcon className="tw-h-4 tw-w-4 tw-text-error" />
          </Button>
        </div>
      ) : (
        <div
          className="tw-cursor-pointer tw-py-1 hover:tw-bg-neutral-100 tw-transition-colors tw-group tw-flex tw-items-center"
          onClick={() => setIsEditing(true)}
          onKeyDown={() => setIsEditing(true)}
          role="button"
          tabIndex={0}
        >
          <p className="tw-font-medium group-hover:tw-text-primary tw-transition-colors">
            {convertDateToUsDateFormat(value)}
          </p>
          <div className="tw-ml-2 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
            <Pencil className="tw-h-4 tw-w-4 tw-text-neutral-600" />
          </div>
        </div>
      )}
    </div>
  );
}
